import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  memo,
  type ReactNode,
} from "react";
import clsx from "clsx";

export type ToastKind = "info" | "success" | "error";

interface ToastEntry {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastContextValue {
  show: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// ⚡ Bolt: Implemented React.memo() for ToastItem component
// 💡 What: Extracted the inline Toast markup into a standalone ToastItem component wrapped in React.memo.
// 🎯 Why: When a new toast is added or removed, the parent `ToastProvider` state updates, causing the entire toast list to re-render. Since toast properties (message, kind) are static, existing toasts don't need to be re-rendered.
// 📊 Impact: Turns O(N) re-renders into O(1) rendering when adding or dismissing a toast, preventing unnecessary CPU usage for existing visible toasts.
// 🔬 Measurement: Use React Profiler to verify that existing toasts do not re-render when a new toast is pushed.
const ToastItem = memo(function ToastItem({ t, dismiss }: { t: ToastEntry; dismiss: (id: number) => void }) {
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered) return;
    const timer = window.setTimeout(() => {
      dismiss(t.id);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [t.id, dismiss, isHovered]);

  return (
    <div
      role={t.kind === "error" ? "alert" : "status"}
      aria-live={t.kind === "error" ? "assertive" : "polite"}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={clsx(
        "pointer-events-auto rounded-md px-4 py-2 text-sm shadow-md flex items-center justify-between gap-3",
        t.kind === "success" && "bg-emerald-600 text-white",
        t.kind === "error" && "bg-red-600 text-white",
        t.kind === "info" && "bg-slate-800 text-white"
      )}
    >
      <span>{t.message}</span>
      <button
        type="button"
        onClick={() => dismiss(t.id)}
        aria-label="Dismiss message"
        title="Dismiss message"
        className={clsx(
          "p-0.5 -mr-1 rounded-sm opacity-80 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
          t.kind === "success" && "focus-visible:ring-emerald-300 focus-visible:ring-offset-emerald-600",
          t.kind === "error" && "focus-visible:ring-red-300 focus-visible:ring-offset-red-600",
          t.kind === "info" && "focus-visible:ring-slate-300 focus-visible:ring-offset-slate-800"
        )}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
          <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
        </svg>
      </button>
    </div>
  );
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const show = useCallback((message: string, kind: ToastKind = "info") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, kind }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex flex-col items-center gap-2 px-4">
        {toasts.map((t) => (
          <ToastItem key={t.id} t={t} dismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
